'use client';
import snapshot from '@snapshot-labs/snapshot.js';
import { useAccount, useBlockNumber } from 'wagmi';
import { GraphQLClient } from 'graphql-request';
import { createContext, useContext, useEffect, useState } from 'react';
import { Web3Provider } from '@ethersproject/providers';
import { useRouter } from 'next/router';

export const DaoContext = createContext({
  daoData: {},
  onDaoUpdate: () => {},
});

const hub = 'https://hub.snapshot.org'; // or https://testnet.snapshot.org for testnet
const client = new snapshot.Client712(hub);
const gqclient = new GraphQLClient('https://hub.snapshot.org/graphql');

export const useSnapshotFunctions = () => {
  const { address: account } = useAccount();
  const { daoData, onDaoUpdate, daoEnsName } = useContext(DaoContext);
  const { selectedProposal, proposals } = daoData;
  const [dataToAdd, setDataToAdd] = useState(null);
  const router = useRouter();

  useEffect(() => {
    if (!dataToAdd) return;
    onDaoUpdate({ ...daoData, ...dataToAdd });
    setDataToAdd(null);
  }, [dataToAdd]);

  const toggleProposalForm = (value) => {
    setDataToAdd({ proposalFormOpen: value });
  };

  const refreshDaoList = async (search) => {
    let pros = [];
    let vp = 0;
    if (daoEnsName) {
      pros = await getProposals(0, search);
    }
    if (account && pros[0]) {
      vp = await getVotingPower(account, pros[0].id);
    } else if (account) {
      vp = await getVotingPower(account);
    }
    const space = await getSpace();
    setDataToAdd({
      proposals: pros,
      votingPower: vp,
      space,
      proposalFormOpen: false,
      selectedProposal: null,
      loaded: true,
    });
  };

  const refreshVotePower = async () => {
    const vp = await getVotingPower(account);
    setDataToAdd({ votingPower: vp });
  };

  const nextPage = async () => {
    try {
      const currentPage = Math.ceil(daoData.proposals?.length / PAGE_SIZE) - 1;
      const pros = await getProposals(currentPage + 1);
      setDataToAdd({ proposals: [...daoData.proposals, ...pros] });
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  const selectProposal = async (proposal) => {
    try {
      if (typeof proposal === 'string') {
        proposal = await getSingleProposal(proposal);
      }
      const currentVote = await getCurrentVote(proposal.id);
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, proposal: proposal.id },
        },
        undefined,
        { shallow: true },
      );
      const vp = await getVotingPower(account, proposal.id);
      setDataToAdd({ votingPower: vp, selectedProposal: { ...proposal, currentVote } });
    } catch (e) {
      console.log(e);
    }
  };

  const backToProposals = async () => {
    delete router.query.proposal;
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
        },
      },
      undefined,
      { shallow: true },
    );
    setDataToAdd({ selectedProposal: null, proposalFormOpen: false });
  };

  const getBlockNumber = async () => {
    const web3 = new Web3Provider(window.ethereum);
    const blockNumber = await web3.getBlockNumber();
    return blockNumber;
  };

  const createProposal = async (title, body, choices, start, end) => {
    //@ts-ignore
    const web3 = new Web3Provider(window.ethereum);
    const blockNumber = await getBlockNumber();
    const [account] = await web3.listAccounts();
    try {
      console.log({
        space: daoEnsName, //'0xnill.eth',
        type: 'single-choice', // define the voting system
        title: title,
        body: body,
        choices: choices,
        start: start,
        end: end,
        snapshot: blockNumber,
        //discussion: 'https://gov.yearn.finance/t/proposal-1-extend-yip-61-vesting-schedule/11187',
        plugins: JSON.stringify({}),
        app: 'my-app',
      });
      const receipt = await client.proposal(web3, account, {
        space: daoEnsName, //'0xnill.eth',
        type: 'single-choice', // define the voting system
        title: title,
        body: body,
        choices: choices,
        start: start,
        end: end,
        snapshot: blockNumber,
        //discussion: 'https://gov.yearn.finance/t/proposal-1-extend-yip-61-vesting-schedule/11187',
        plugins: JSON.stringify({}),
        app: 'my-app', // provide the name of your project which is using this snapshot.js integration
      });
      refreshDaoList();
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  const vote = async (choice) => {
    const { id, space } = selectedProposal || {};
    const web3 = new Web3Provider(window.ethereum);
    const [account] = await web3.listAccounts();

    const receipt = await client.vote(web3, account, {
      space: space.id,
      proposal: id,
      type: 'single-choice',
      choice: choice,
    });
    const updatedProposal = await getSingleProposal(id);
    await selectProposal(updatedProposal);
  };

  const getSpace = async () => {
    const { space } = await gqclient.request(`query {
        space(id: "${daoEnsName}") {
          id
          name
          about
          network
          symbol
          strategies {
            name
            network
            params
          }
          validation {
            name
            params
          }
          members
        }
      }`);
    return space;
  };

  const getSingleProposal = async (id) => {
    try {
      const proposal = await gqclient.request(`query {
            proposal(id:"${id}") {
                id
                title
                body
                choices
                start
                end
                snapshot
                state
                author
                scores
                scores_total
                space {
                  id
                  name
                }
            }
          }`);
      return proposal?.proposal;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  const PAGE_SIZE = 50;
  const getProposals = async (page, search) => {
    try {
      const result = await gqclient.request(`query Proposals {
            proposals(
              first: ${PAGE_SIZE},
              skip: ${page * PAGE_SIZE},
              where: {
                space_in: ["${daoEnsName}"],
                ${search ? `title_contains: "${search}",` : ''}
              },
              orderBy: "created",
              orderDirection: desc
            ) {
              id
              title
              body
              choices
              start
              end
              snapshot
              state
              author
              scores
              scores_total
              space {
                id
                name
              }
            }
          }`);

      return result?.proposals;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  const getVotingPower = async (
    walletAddress, // string
    proposalId, //string
  ) => {
    try {
      return (
        await gqclient.request(`query {
      vp (
        voter: "${walletAddress}"
        space: "${daoEnsName}"
        ${proposalId ? `proposal: "${proposalId}"` : ''}
      ) {
        vp
        vp_by_strategy
        vp_state
      } 
    }`)
      )?.vp.vp;
    } catch (e) {
      console.log(e);
      return 0;
    }
  };

  const getCurrentVote = async (id) => {
    try {
      return (
        await gqclient.request(`query Votes {
            votes (
              first: 1000
              skip: 0
              where: {
                proposal: "${id}"
                voter: "${account}"
              }
              orderBy: "created",
              orderDirection: desc
            ) {
              id
              voter
              created
              proposal {
                id
              }
              choice
              space {
                id
              }
            }
          }`)
      ).votes[0]?.choice;
    } catch (e) {
      return 0;
    }
  };

  const getAllVotes = async () => {
    return await gqclient.request(`query Votes {
        votes (
          first: 1000
          skip: 0
          where: {
            proposal: "${selectedProposal.id}"
          }
          orderBy: "created",
          orderDirection: desc
        ) {
          id
          voter
          created
          proposal {
            id
          }
          vp
          choice
          space {
            id
          }
        }
      }`);
  };

  return {
    getProposals,
    getVotingPower,
    selectProposal,
    backToProposals,
    refreshDaoList,
    vote,
    toggleProposalForm,
    createProposal,
    getBlockNumber,
    getAllVotes,
    nextPage,
    refreshVotePower,
  };
};
